package dev.danko.roblox_craft_ai.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.FontWeight
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextOverflow
import com.varabyte.kobweb.compose.css.WhiteSpace
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.style.breakpoint.displayIfAtLeast
import com.varabyte.kobweb.silk.style.breakpoint.displayUntil
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.danko.roblox_craft_ai.state.AuthState
import dev.danko.roblox_craft_ai.state.UiModeController
import dev.danko.roblox_craft_ai.uikit.components.AppButton
import dev.danko.roblox_craft_ai.uikit.components.AppButtonSize
import dev.danko.roblox_craft_ai.uikit.components.AppButtonVariant
import dev.danko.roblox_craft_ai.uikit.components.ToastManager
import dev.danko.roblox_craft_ai.uikit.i18n.AppLocale
import dev.danko.roblox_craft_ai.uikit.i18n.LocaleController
import dev.danko.roblox_craft_ai.uikit.i18n.strings
import dev.danko.roblox_craft_ai.uikit.icons.AppIcon
import dev.danko.roblox_craft_ai.uikit.icons.Icon
import dev.danko.roblox_craft_ai.uikit.theme.LocalAppTheme
import dev.danko.roblox_craft_ai.uikit.theme.toAppPalette
import kotlinx.browser.document
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.LineStyle
import org.jetbrains.compose.web.css.Position
import org.jetbrains.compose.web.css.cssRem
import org.jetbrains.compose.web.css.percent
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.css.vw
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.attrsModifier
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Header
import org.jetbrains.compose.web.dom.Nav
import org.w3c.dom.Node
import org.w3c.dom.events.Event

enum class SiteSection(val path: String) {
    WORKSHOP("/"),
    IDEAS("/prompts"),
    PRICING("/pricing"),
    SETTINGS("/settings"),
    ADMIN("/admin"),
    OTHER("")
}

val SiteHeaderStyle = CssStyle.base {
    val palette = colorMode.toAppPalette()
    Modifier
        .fillMaxWidth()
        .borderBottom(1.px, LineStyle.Solid, palette.border)
        .backgroundColor(palette.background)
        .position(Position.Sticky)
        .top(0.px)
        .zIndex(40)
}

val NavLinkStyle = CssStyle {
    base {
        val palette = colorMode.toAppPalette()
        Modifier
            .height(36.px)
            .padding(leftRight = 14.px)
            .borderRadius(8.px)
            .border(0.px, LineStyle.None, Colors.Transparent)
            .backgroundColor(Colors.Transparent)
            .color(palette.textSecondary)
            .fontSize(14.px)
            .fontWeight(FontWeight.SemiBold)
            .cursor(Cursor.Pointer)
            .whiteSpace(WhiteSpace.NoWrap)
    }
    cssRule(":hover") {
        val palette = colorMode.toAppPalette()
        Modifier.color(palette.textPrimary).backgroundColor(palette.surface)
    }
}

val PillStyle = CssStyle {
    base {
        val palette = colorMode.toAppPalette()
        Modifier
            .height(36.px)
            .padding(leftRight = 12.px)
            .borderRadius(999.px)
            .border(1.px, LineStyle.Solid, palette.border)
            .backgroundColor(palette.surface)
            .color(palette.textPrimary)
            .fontSize(13.px)
            .fontWeight(FontWeight.Bold)
            .cursor(Cursor.Pointer)
            .whiteSpace(WhiteSpace.NoWrap)
            .display(org.jetbrains.compose.web.css.DisplayStyle.Flex)
            .alignItems(org.jetbrains.compose.web.css.AlignItems.Center)
            .gap(8.px)
    }
    cssRule(":hover") {
        val palette = colorMode.toAppPalette()
        Modifier.border(1.px, LineStyle.Solid, palette.borderStrong)
    }
}

val MenuItemStyle = CssStyle {
    base {
        val palette = colorMode.toAppPalette()
        Modifier
            .fillMaxWidth()
            .minHeight(44.px)
            .padding(leftRight = 12.px)
            .borderRadius(10.px)
            .border(0.px, LineStyle.None, Colors.Transparent)
            .backgroundColor(Colors.Transparent)
            .color(palette.textPrimary)
            .fontSize(14.px)
            .fontWeight(FontWeight.Medium)
            .cursor(Cursor.Pointer)
            .display(org.jetbrains.compose.web.css.DisplayStyle.Flex)
            .alignItems(org.jetbrains.compose.web.css.AlignItems.Center)
            .gap(12.px)
            .textAlign(com.varabyte.kobweb.compose.css.TextAlign.Left)
    }
    cssRule(":hover") {
        val palette = colorMode.toAppPalette()
        Modifier.backgroundColor(palette.surfaceElevated)
    }
}

/**
 * The one header every page shares (style A): brand and sections on the left; Studio state, credits and the
 * account menu on the right. Language, theme, simple mode, settings, admin and sign-out live in the menu, and on
 * narrow screens the sections move into it too. [status] lets a page add its own indicator next to the credits.
 */
@Composable
fun SiteHeader(
    active: SiteSection,
    subtitle: String? = null,
    status: (@Composable () -> Unit)? = null
) {
    val s = strings()
    val palette = LocalAppTheme.current.colors
    val ctx = rememberPageContext()
    var menuOpen by remember { mutableStateOf(false) }

    fun go(section: SiteSection) {
        menuOpen = false
        if (section != active) ctx.router.navigateTo(section.path)
    }

    val sections = listOf(
        SiteSection.WORKSHOP to s.nav.workshop.removePrefix("🎮 "),
        SiteSection.IDEAS to s.header.presets.removePrefix("📚 "),
        SiteSection.PRICING to s.billing.navPricing.removePrefix("💎 ")
    )

    Header(SiteHeaderStyle.toModifier().toAttrs()) {
        Row(
            Modifier.fillMaxWidth().maxWidth(1280.px).styleModifier { property("margin", "0 auto") }.padding(topBottom = 12.px, leftRight = 20.px).gap(20.px),
            verticalAlignment = Alignment.CenterVertically
        ) {
            Button(
                Modifier
                    .display(org.jetbrains.compose.web.css.DisplayStyle.Flex)
                    .alignItems(org.jetbrains.compose.web.css.AlignItems.Center)
                    .gap(10.px)
                    .border(0.px, LineStyle.None, Colors.Transparent)
                    .backgroundColor(Colors.Transparent)
                    .color(palette.textPrimary)
                    .padding(0.px)
                    .cursor(Cursor.Pointer)
                    .onClick { go(SiteSection.WORKSHOP) }
                    .toAttrs { attr("type", "button"); attr("aria-label", s.header.appTitle) }
            ) {
                Box(
                    Modifier.size(36.px).borderRadius(10.px).backgroundColor(palette.accent).color(Colors.White),
                    contentAlignment = Alignment.Center
                ) { Icon(AppIcon.LOGO, size = 20) }
                Column(Modifier.displayIfAtLeast(Breakpoint.SM)) {
                    SpanText(s.header.appTitle, Modifier.fontSize(17.px).fontWeight(FontWeight.ExtraBold))
                    if (subtitle != null) SpanText(subtitle, Modifier.fontSize(12.px).color(palette.textSecondary))
                }
            }

            Nav(Modifier.displayIfAtLeast(Breakpoint.MD).toAttrs { attr("aria-label", s.nav.menu) }) {
                Row(Modifier.gap(4.px)) {
                    sections.forEach { (section, label) ->
                        Button(
                            NavLinkStyle.toModifier()
                                .then(if (section == active) Modifier.backgroundColor(palette.surfaceElevated).color(palette.textPrimary) else Modifier)
                                .onClick { go(section) }
                                .toAttrs {
                                    attr("type", "button")
                                    if (section == active) attr("aria-current", "page")
                                }
                        ) { SpanText(label) }
                    }
                }
            }

            Spacer()

            Row(Modifier.gap(8.px), verticalAlignment = Alignment.CenterVertically) {
                status?.invoke()
                CreditsPill()
                if (AuthState.user == null) {
                    AppButton(
                        text = s.auth.signIn,
                        onClick = { AuthState.loginDialogOpen = true },
                        variant = AppButtonVariant.SECONDARY,
                        size = AppButtonSize.SM,
                        icon = AppIcon.USER,
                        modifier = Modifier.displayIfAtLeast(Breakpoint.SM)
                    )
                }
                AccountMenu(
                    open = menuOpen,
                    onToggle = { menuOpen = !menuOpen },
                    onClose = { menuOpen = false },
                    sections = sections,
                    active = active,
                    go = ::go
                )
            }
        }
    }
}

@Composable
private fun CreditsPill() {
    val ctx = rememberPageContext()
    val usage = AuthState.usage ?: return
    val limit = usage.creditsLimit ?: return
    val left = (limit - usage.creditsUsed).coerceAtLeast(0)
    val available = left + usage.purchasedCredits
    val resetsOn = kotlin.js.Date(usage.periodEndsAtMs.toDouble()).toLocaleDateString(LocaleController.current.code)
    val palette = LocalAppTheme.current.colors
    val tooltip = strings().auth.creditsTooltip(left, limit, usage.purchasedCredits, resetsOn)
    Button(
        PillStyle.toModifier()
            .then(if (available == 0) Modifier.color(palette.statusFailed) else Modifier)
            .onClick { ctx.router.navigateTo("/pricing") }
            .toAttrs {
                attr("type", "button")
                attr("title", tooltip)
            }
    ) {
        Box(Modifier.color(palette.statusPending)) { Icon(AppIcon.ZAP, size = 16) }
        SpanText("$available")
    }
}

/** A small rounded pill with a status dot, used by pages for their indicator next to the credits. */
@Composable
fun StatusPill(label: String, ok: Boolean, title: String, onClick: () -> Unit) {
    val palette = LocalAppTheme.current.colors
    Button(
        PillStyle.toModifier()
            .color(palette.textSecondary)
            .fontWeight(FontWeight.SemiBold)
            .onClick { onClick() }
            .toAttrs {
                attr("type", "button")
                attr("title", title)
            }
    ) {
        Box(Modifier.size(8.px).borderRadius(50.percent).backgroundColor(if (ok) palette.statusReady else palette.statusPending))
        SpanText(label)
    }
}

@Composable
private fun AccountMenu(
    open: Boolean,
    onToggle: () -> Unit,
    onClose: () -> Unit,
    sections: List<Pair<SiteSection, String>>,
    active: SiteSection,
    go: (SiteSection) -> Unit
) {
    val s = strings()
    val palette = LocalAppTheme.current.colors
    val scope = rememberCoroutineScope()
    var colorMode by ColorMode.currentState
    var container by remember { mutableStateOf<org.w3c.dom.HTMLElement?>(null) }
    val user = AuthState.user

    // Close on a click outside the menu or on Escape.
    DisposableEffect(open) {
        if (!open) return@DisposableEffect onDispose { }
        val outside: (Event) -> Unit = { event ->
            val target = event.target as? Node
            if (target != null && container?.contains(target) == false) onClose()
        }
        val escape: (Event) -> Unit = { event -> if (event.asDynamic().key == "Escape") onClose() }
        document.addEventListener("mousedown", outside)
        document.addEventListener("keydown", escape)
        onDispose {
            document.removeEventListener("mousedown", outside)
            document.removeEventListener("keydown", escape)
        }
    }

    Div(Modifier.position(Position.Relative).toAttrs { ref { element -> container = element; onDispose { container = null } } }) {
        Button(
            PillStyle.toModifier()
                .padding(left = 4.px, right = 8.px)
                .color(palette.textSecondary)
                .onClick { onToggle() }
                .toAttrs {
                    attr("type", "button")
                    attr("aria-haspopup", "menu")
                    attr("aria-expanded", open.toString())
                    attr("aria-label", if (user != null) s.auth.accountTooltip else s.nav.menu)
                }
        ) {
            if (user != null) {
                Box(
                    Modifier.size(28.px).borderRadius(50.percent).backgroundColor(palette.surfaceElevated).color(palette.accentText)
                        .fontSize(13.px).fontWeight(FontWeight.Bold),
                    contentAlignment = Alignment.Center
                ) { SpanText(user.email.take(1).uppercase()) }
            } else {
                Box(Modifier.padding(left = 6.px)) { Icon(AppIcon.MENU, size = 18) }
            }
            Icon(AppIcon.CHEVRON_DOWN, size = 16)
        }

        if (open) {
            Column(
                Modifier
                    .position(Position.Absolute)
                    .right(0.px)
                    .top(44.px)
                    .width(320.px)
                    .maxWidth(90.vw)
                    .padding(8.px)
                    .borderRadius(16.px)
                    .border(1.px, LineStyle.Solid, palette.borderStrong)
                    .backgroundColor(palette.surface)
                    .styleModifier { property("box-shadow", "0 16px 40px rgba(0,0,0,0.45)") }
                    .gap(2.px)
                    .zIndex(50)
                    .classNames("rc-fade-in")
                    .attrsModifier { attr("role", "menu") }
            ) {
                if (user != null) {
                    Row(Modifier.padding(12.px).gap(12.px), verticalAlignment = Alignment.CenterVertically) {
                        Box(
                            Modifier.size(40.px).borderRadius(50.percent).backgroundColor(palette.surfaceElevated).color(palette.accentText).fontWeight(FontWeight.Bold),
                            contentAlignment = Alignment.Center
                        ) { SpanText(user.email.take(1).uppercase()) }
                        Column(Modifier.gap(2.px).minWidth(0.px)) {
                            SpanText(
                                user.email,
                                Modifier.fontSize(14.px).fontWeight(FontWeight.Bold).overflow(Overflow.Hidden).textOverflow(TextOverflow.Ellipsis).whiteSpace(WhiteSpace.NoWrap)
                            )
                            AuthState.planId?.let { plan ->
                                SpanText(s.nav.plan(plan.replaceFirstChar { it.uppercase() }), Modifier.fontSize(13.px).color(palette.textSecondary))
                            }
                        }
                    }
                    CreditsMeter()
                }

                // On narrow screens the sections live here.
                Column(Modifier.fillMaxWidth().displayUntil(Breakpoint.MD)) {
                    sections.forEach { (section, label) ->
                        MenuRow(label = label, icon = null, value = if (section == active) "•" else null) { go(section) }
                    }
                    Divider()
                }

                Column(Modifier.fillMaxWidth().padding(leftRight = 12.px, topBottom = 8.px).gap(8.px)) {
                    Row(Modifier.gap(10.px).color(palette.textSecondary), verticalAlignment = Alignment.CenterVertically) {
                        Icon(AppIcon.GLOBE)
                        SpanText(s.common.language, Modifier.fontSize(14.px).color(palette.textPrimary))
                    }
                    Row(Modifier.gap(6.px)) {
                        AppLocale.entries.forEach { locale ->
                            AppButton(
                                text = locale.shortLabel,
                                onClick = { LocaleController.select(locale) },
                                variant = if (locale == LocaleController.current) AppButtonVariant.PRIMARY else AppButtonVariant.OUTLINE,
                                size = AppButtonSize.SM,
                                modifier = Modifier.flexGrow(1).title(locale.nativeName)
                            )
                        }
                    }
                }
                MenuRow(
                    label = s.settingsPage.themeLabel,
                    icon = if (colorMode.isDark) AppIcon.MOON else AppIcon.SUN,
                    value = if (colorMode.isDark) s.settingsPage.themeDark.drop(3) else s.settingsPage.themeLight.drop(3)
                ) { colorMode = colorMode.opposite }
                MenuRow(
                    label = s.common.simpleMode,
                    icon = AppIcon.SLIDERS,
                    value = if (UiModeController.simpleMode) s.nav.on else s.nav.off,
                    title = s.common.simpleModeHint
                ) { UiModeController.toggle() }
                MenuRow(label = s.header.settings, icon = AppIcon.SETTINGS) { go(SiteSection.SETTINGS) }
                if (AuthState.isAdmin) {
                    MenuRow(label = s.admin.nav.removePrefix("🛠 "), icon = AppIcon.SHIELD) { go(SiteSection.ADMIN) }
                }
                Divider()
                if (user != null) {
                    MenuRow(label = s.auth.signOut, icon = AppIcon.LOG_OUT, danger = true) {
                        onClose()
                        scope.launch {
                            AuthState.signOut()
                            ToastManager.info(s.auth.signedOut)
                        }
                    }
                } else {
                    MenuRow(label = s.auth.signIn, icon = AppIcon.USER) {
                        onClose()
                        AuthState.loginDialogOpen = true
                    }
                }
            }
        }
    }
}

@Composable
private fun CreditsMeter() {
    val usage = AuthState.usage ?: return
    val limit = usage.creditsLimit ?: return
    val palette = LocalAppTheme.current.colors
    val left = (limit - usage.creditsUsed).coerceAtLeast(0)
    val resetsOn = kotlin.js.Date(usage.periodEndsAtMs.toDouble()).toLocaleDateString(LocaleController.current.code)
    Column(Modifier.margin(left = 8.px, right = 8.px, bottom = 8.px).padding(12.px).borderRadius(12.px).backgroundColor(palette.background).gap(8.px)) {
        Row(Modifier.fillMaxWidth().fontSize(13.px)) {
            Box(Modifier.color(palette.statusPending)) { Icon(AppIcon.ZAP, size = 16) }
            Spacer()
            SpanText("$left / $limit", Modifier.fontWeight(FontWeight.Bold))
        }
        Box(Modifier.fillMaxWidth().height(6.px).borderRadius(999.px).backgroundColor(palette.border).overflow(Overflow.Hidden)) {
            Box(Modifier.width((left * 100.0 / limit.coerceAtLeast(1)).percent).height(6.px).backgroundColor(palette.accent))
        }
        SpanText(
            strings().auth.creditsTooltip(left, limit, usage.purchasedCredits, resetsOn),
            Modifier.fontSize(12.px).color(palette.textSecondary)
        )
    }
}

@Composable
private fun Divider() {
    Box(Modifier.fillMaxWidth().height(1.px).margin(topBottom = 6.px).backgroundColor(LocalAppTheme.current.colors.border))
}

@Composable
private fun MenuRow(
    label: String,
    icon: AppIcon?,
    value: String? = null,
    title: String? = null,
    danger: Boolean = false,
    onClick: () -> Unit
) {
    val palette = LocalAppTheme.current.colors
    Button(
        MenuItemStyle.toModifier()
            .then(if (danger) Modifier.color(palette.statusFailed) else Modifier)
            .onClick { onClick() }
            .toAttrs {
                attr("type", "button")
                attr("role", "menuitem")
                title?.let { attr("title", it) }
            }
    ) {
        Box(Modifier.width(20.px).color(if (danger) palette.statusFailed else palette.textSecondary)) {
            if (icon != null) Icon(icon)
        }
        SpanText(label, Modifier.flexGrow(1))
        if (value != null) SpanText(value, Modifier.fontSize(13.px).color(palette.textSecondary))
    }
}
